/** The daily circuit rolls over at midnight in Rome. */

const ROME = 'Europe/Rome';

// Made once: building a formatter costs far more than using one (these are asked for every board refresh).
let dayFormat: Intl.DateTimeFormat | null = null;
let clockFormat: Intl.DateTimeFormat | null = null;

/** "2026-10-06": the day in Rome, from the date's parts (never from a locale's own pattern, which can change). */
export function romeDateString(now: Date = new Date()): string {
  // Not format(): en-CA used to give YYYY-MM-DD, but some browsers (Chrome 110-114) write it M/D/YYYY, which
  // then broke every date after it (the game wouldn't start).
  dayFormat ??= new Intl.DateTimeFormat('en-US', { timeZone: ROME, year: 'numeric', month: '2-digit', day: '2-digit', numberingSystem: 'latn' });
  let y = '';
  let m = '';
  let d = '';
  for (const p of dayFormat.formatToParts(now)) {
    if (p.type === 'year') y = p.value;
    else if (p.type === 'month') m = p.value;
    else if (p.type === 'day') d = p.value;
  }
  return `${y.padStart(4, '0')}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

export function isValidDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s + 'T12:00:00Z'));
}

/** Milliseconds until the next midnight in Rome. */
export function msUntilRomeMidnight(now: Date = new Date()): number {
  clockFormat ??= new Intl.DateTimeFormat('en-GB', { timeZone: ROME, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  const parts = clockFormat
    .formatToParts(now)
    .reduce<Record<string, number>>((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = Number(p.value);
      return acc;
    }, {});
  const elapsed = ((parts.hour ?? 0) * 3600 + (parts.minute ?? 0) * 60 + (parts.second ?? 0)) * 1000 + now.getMilliseconds();
  return 24 * 3600 * 1000 - elapsed;
}

/** The hour in Rome (0–23). */
export function romeHour(now: Date = new Date()): number {
  return Math.min(23, Math.floor((24 * 3600 * 1000 - msUntilRomeMidnight(now)) / 3_600_000));
}

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** "27 september 2026" */
export function formatDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export function shiftDate(date: string, days: number): string {
  const t = Date.parse(date + 'T12:00:00Z') + days * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** "monday 28 september", the way a race poster dates its Sunday. */
export function formatPosterDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return `${WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTHS[m - 1]}`;
}
